import type { AiChatPendingProposal, AiChatSession } from '../../services/aiChatHistoryService'
import type { AiMagicChatTurn } from '../../services/aiService'
import type { AiOperatingMode } from '../../types/ai'

export type FollowUpAnchor = {
  snippet: string
  source: 'assistant' | 'user' | 'none'
}

export type AiMagicAutoOpenInput = {
  activeChatId: string | null
  effectiveOperatingMode: AiOperatingMode
  isDraftingNewChat: boolean
  loadedChatIdByMode: Record<AiOperatingMode, string | null>
}

export type AiMagicRunScope = {
  mode: AiOperatingMode
  initialChatId: string | null
  resolvedChatId: string | null
  userTurnId: string | null
}

export type AiMagicVisibleModeState = {
  activeChatId: string | null
  isDraftingNewChat: boolean
  chatTurns: AiMagicChatTurn[]
}

const FOLLOW_UP_SNIPPET_LIMIT = 52
const FOLLOW_UP_MIN_WORD_BOUNDARY = 24

// Keeps composer placeholders short and avoids doubled punctuation when the
// caller adds the quoted ellipsis.
export const truncateFollowUpSnippet = (raw: string): string => {
  const cleaned = raw.replace(/```[\s\S]*?```/g, ' ').replace(/\s+/g, ' ').trim()
  if (!cleaned) return ''
  if (cleaned.length <= FOLLOW_UP_SNIPPET_LIMIT) return cleaned.replace(/[.,;:!?…]+$/, '')
  const cut = cleaned.slice(0, FOLLOW_UP_SNIPPET_LIMIT)
  const lastSpace = cut.lastIndexOf(' ')
  const clipped = lastSpace > FOLLOW_UP_MIN_WORD_BOUNDARY ? cut.slice(0, lastSpace) : cut
  return clipped.replace(/[.,;:!?…]+$/, '')
}

export const composeFollowUpAnchor = (turns: AiMagicChatTurn[]): FollowUpAnchor => {
  for (let i = turns.length - 1; i >= 0; i--) {
    const turn = turns[i]
    if (turn.role === 'ASSISTANT' && turn.text?.trim()) {
      const snippet = truncateFollowUpSnippet(turn.text)
      if (snippet) return { snippet, source: 'assistant' }
    }
  }
  for (let i = turns.length - 1; i >= 0; i--) {
    const turn = turns[i]
    if (turn.role === 'USER' && turn.text?.trim()) {
      const snippet = truncateFollowUpSnippet(turn.text)
      if (snippet) return { snippet, source: 'user' }
    }
  }
  return { snippet: '', source: 'none' }
}

export const findOpenClarificationTurn = (turns: AiMagicChatTurn[]): AiMagicChatTurn | undefined => {
  const lastTurn = [...turns].reverse().find((turn) => turn.role === 'USER' || turn.role === 'ASSISTANT')
  return lastTurn?.role === 'ASSISTANT'
    && lastTurn.status === 'PENDING_CLARIFICATION'
    && (lastTurn.routing?.clarification || lastTurn.routing?.interviewMode)
    ? lastTurn
    : undefined
}

export const isLegacyEmptyAiChatDraft = (session: AiChatSession): boolean =>
  (!session.title.trim() || session.title === 'New chat')
  && session.turns.length === 0
  && session.attachments.length === 0

/** Returns only a still-actionable server-mirrored proposal. */
export const getActivePendingProposal = (
  pendingProposal: AiChatPendingProposal | null | undefined,
  now = Date.now(),
): AiChatPendingProposal | null => (
  pendingProposal
  && Number.isFinite(pendingProposal.expiresAt)
  && pendingProposal.expiresAt > now
    ? pendingProposal
    : null
)

/**
 * A pending draft normally has its assistant turn in the transcript. Keep a
 * defensive synthetic anchor for older/interrupted writes so the detailed card
 * is never stranded after a cross-device rehydrate.
 */
export const withPendingProposalAnchor = (
  turns: AiMagicChatTurn[],
  pendingProposal: AiChatPendingProposal | null,
): AiMagicChatTurn[] => {
  if (!pendingProposal || turns.some((turn) => turn.id === pendingProposal.turnId)) return turns
  return [...turns, {
    id: pendingProposal.turnId,
    role: 'ASSISTANT',
    text: pendingProposal.summary.heading,
    proposalSummary: pendingProposal.summary,
  }]
}

export const getVisibleAiMagicSessions = (
  sessions: AiChatSession[],
  forcedOperatingMode?: AiOperatingMode | null,
): AiChatSession[] => {
  const durableSessions = sessions.filter((session) => !isLegacyEmptyAiChatDraft(session))
  return forcedOperatingMode
    ? durableSessions.filter((session) => session.operatingMode === forcedOperatingMode)
    : durableSessions
}

export const selectAiMagicSessionToAutoOpen = (
  visibleSessions: AiChatSession[],
  input: AiMagicAutoOpenInput,
): AiChatSession | null => {
  const selected = visibleSessions.find((session) => session.id === input.activeChatId)
  if (selected) {
    return input.loadedChatIdByMode[input.effectiveOperatingMode] !== selected.id ? selected : null
  }
  if (input.activeChatId || input.isDraftingNewChat || input.loadedChatIdByMode[input.effectiveOperatingMode] !== null) {
    return null
  }
  return visibleSessions.find((session) => session.operatingMode === input.effectiveOperatingMode) ?? null
}

export const aiMagicRunChatId = (scope: AiMagicRunScope): string | null =>
  scope.resolvedChatId ?? scope.initialChatId

export const isAiMagicRunVisible = (
  state: AiMagicVisibleModeState,
  scope: AiMagicRunScope,
): boolean => {
  const chatId = aiMagicRunChatId(scope)
  if (chatId) return state.activeChatId === chatId
  if (state.activeChatId !== null || !state.isDraftingNewChat) return false
  if (!scope.userTurnId) return true
  return state.chatTurns.some((turn) => turn.id === scope.userTurnId)
}

export const canPromoteAiMagicDraftRun = (
  state: AiMagicVisibleModeState,
  scope: AiMagicRunScope,
): boolean => {
  if (scope.initialChatId || scope.resolvedChatId) return false
  if (!state.isDraftingNewChat || state.activeChatId !== null) return false
  if (!scope.userTurnId) return true
  return state.chatTurns.some((turn) => turn.id === scope.userTurnId)
}
